<template>
  <div id="app">
    <header class="app-header" v-if="showHeader">
      <div class="container">
        <div class="header-content">
          <router-link to="/" class="logo">
            <span class="logo-icon">⚡</span>
            <span class="logo-text">电技95 · 聚会平台</span>
          </router-link>
          <nav class="main-nav">
            <router-link to="/" class="nav-link">首页</router-link>
            <router-link to="/about" class="nav-link">关于</router-link>
          </nav>
          <div class="header-actions">
            <button @click="handleLogout" class="btn-logout">退出</button>
          </div>
        </div>
      </div>
    </header>
    <main class="app-main">
      <router-view />
    </main>
    <!-- 背景音乐浮动控制 -->
    <div class="music-float" v-if="musicVisible">
      <div class="music-track" v-if="trackName">{{ trackName }}</div>
      <button class="music-btn" :class="{ playing: musicPlaying }" @click="handleMusicToggle" :title="musicPlaying ? '暂停音乐' : '播放音乐'">
        <span class="music-icon">{{ musicPlaying ? '🎵' : '🔇' }}</span>
      </button>
      <button class="music-btn music-next" @click="handleMusicNext" title="下一首">⏭</button>
    </div>
    <footer class="app-footer" v-if="showHeader">
      <div class="container">
        <p>中国矿业大学 · 电气技术与自动化 1995级</p>
        <p class="footer-sub">相识三十一年 · 回路终闭合</p>
        <p class="icp-beian">
          <a href="https://beian.miit.gov.cn" target="_blank">沪ICP备2023009132号-1</a>
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { logout, isLoggedIn } from '@/utils/auth'
import * as music from '@/utils/music'

export default {
  name: 'App',
  data() {
    return {
      showHeader: false,
      musicVisible: false,
      musicPlaying: false,
      trackName: ''
    }
  },
  mounted() {
    // 恢复登录态
    if (isLoggedIn()) {
      this.showHeader = true
    }

    // 监听路由变化，同步 header 显示
    this.$watch(() => this.$route.path, () => {
      this.showHeader = isLoggedIn()
      this.syncMusic()
    })

    // 订阅音乐状态
    this._unsubMusic = music.onStateChange(() => {
      this.musicPlaying = music.isPlaying()
      const t = music.currentTrack()
      this.trackName = t ? t.name : ''
    })

    this.syncMusic()
  },
  beforeUnmount() {
    if (this._unsubMusic) this._unsubMusic()
  },
  methods: {
    // 进入活动相关页面时自动随机播放，离开时停止
    syncMusic() {
      const isEventPage = this.$route.path.startsWith('/event/')
      this.musicVisible = isEventPage
      if (isEventPage) {
        if (!music.isPlaying()) {
          music.playRandom()
        }
      } else {
        music.stop()
      }
      this.musicPlaying = music.isPlaying()
      const t = music.currentTrack()
      this.trackName = t ? t.name : ''
    },
    handleMusicToggle() {
      music.toggle()
    },
    handleMusicNext() {
      music.next()
    },
    handleLogout() {
      logout()
      this.showHeader = false
      this.musicVisible = false
      music.stop()
      this.$router.push('/login')
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: #F0F4F8;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.app-header {
  background: white;
  box-shadow: 0 2px 4px rgba(0, 91, 175, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: #333;
  font-size: 18px;
  font-weight: 700;
}

.logo-icon {
  font-size: 28px;
}

.logo-text {
  background: linear-gradient(135deg, #005BAF 0%, #003D7A 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.main-nav {
  display: flex;
  gap: 30px;
}

.nav-link {
  text-decoration: none;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: color 0.3s;
  position: relative;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: #005BAF;
}

.nav-link.router-link-active::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  right: 0;
  height: 2px;
  background: #005BAF;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-logout {
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: transparent;
  border: 1px solid #005BAF;
  color: #005BAF;
}

.btn-logout:hover {
  background: #005BAF;
  color: white;
}

.app-main {
  flex: 1;
}

.app-footer {
  background: linear-gradient(135deg, #005BAF 0%, #003D7A 100%);
  color: white;
  padding: 30px 0;
  text-align: center;
  margin-top: auto;
}

.app-footer p {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
}

.footer-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
}

.icp-beian {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 10px;
}

.icp-beian a {
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
}

.icp-beian a:hover {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: underline;
}

/* 背景音乐浮动控制 */
.music-float {
  position: fixed;
  right: 20px;
  bottom: 30px;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 10px;
}

.music-track {
  background: rgba(0, 59, 122, 0.85);
  color: #fff;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 16px;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.music-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: #005BAF;
  color: #fff;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 91, 175, 0.35);
  transition: transform 0.2s, background 0.2s;
}

.music-btn:hover {
  transform: scale(1.08);
  background: #003D7A;
}

.music-btn.playing .music-icon {
  animation: music-spin 4s linear infinite;
  display: inline-block;
}

.music-next {
  width: 40px;
  height: 40px;
  font-size: 15px;
  background: rgba(0, 91, 175, 0.75);
  box-shadow: 0 4px 10px rgba(0, 91, 175, 0.25);
}

@keyframes music-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .music-float {
    right: 14px;
    bottom: 20px;
  }
  .music-track {
    max-width: 120px;
    font-size: 11px;
  }
  .music-btn {
    width: 44px;
    height: 44px;
    font-size: 18px;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-wrap: wrap;
    height: auto;
    padding: 15px 0;
    gap: 15px;
  }

  .logo-text {
    font-size: 14px;
  }

  .main-nav {
    order: 3;
    width: 100%;
    justify-content: center;
    gap: 20px;
  }

  .header-actions {
    order: 2;
  }
}
</style>
